// src/components/Footer.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicSiteWebService } from "../services/publicSiteWebService";

const Footer = () => {
  const [pageData, setPageData] = useState({
    parametres: null,
    contact: null,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await publicSiteWebService.getPublicData();
        setPageData({
          parametres: data.parametres,
          contact: data.contact,
        });
      } catch (error) {
        console.error("Erreur de chargement du footer:", error);
      }
    };
    loadData();
  }, []);

  const currentYear = new Date().getFullYear();
  const params = pageData.parametres || {};
  const contact = pageData.contact || {};
  
  // Utiliser les coordonnées du backend ou les valeurs par défaut
  const email = contact.email || "contact@enumera.com";
  const telephone = contact.telephone || "+226 55 87 87 01";

  const footerSections = [
    {
      title: "Produit",
      links: [
        { icon: "ti-info-circle", label: "À propos", path: "/about" },
        { icon: "ti-layout-dashboard", label: "Fonctionnalités", path: "/features" },
        { icon: "ti-device-mobile", label: "Applications mobiles", path: "/apps" },
      ]
    },
    {
      title: "Support",
      links: [
        { icon: "ti-help-circle", label: "Centre d'aide", path: "/help" },
        
        { icon: "ti-mail", label: "Nous contacter", path: "/contact" },
      ]
    },
    {
      title: "Légal",
      links: [
        { icon: "ti-shield-lock", label: "Confidentialité", path: "/privacy" },
        { icon: "ti-file-text", label: "Conditions", path: "/terms" },

      ]
    }
  ];

  // Réseaux sociaux dynamiques
  const socialLinks = [
    { icon: "ti-brand-facebook", href: contact.facebook_url || "#", label: "Facebook" },
    { icon: "ti-brand-whatsapp", href: "https://wa.me/55878701", label: "WhatsApp" },
    { icon: "ti-brand-linkedin", href: contact.linkedin_url || "#", label: "LinkedIn" },
    { icon: "ti-brand-youtube", href: contact.youtube_url || "#", label: "YouTube" },
    { icon: "ti-brand-x", href: contact.twitter_url || "#", label: "X (Twitter)" },
  ].filter(link => link.href && link.href !== "#");

  return (
    <footer className="footer">
      {/* Filet dégradé bleu → or : la signature du logo */}
      <div className="footer-top-line" />

      <div className="footer-container">
        {/* Brand */}
        <div className="footer-brand">
          <div className="footer-logo">
            <div className="footer-logo-icon">
              <i className="ti ti-school" />
            </div>
            <span>{params.nom_editeur || "Enumera"}</span>
          </div>
          <p className="footer-description">
            {params.slogan || "La plateforme SaaS de suivi scolaire qui rapproche l'école des familles."}
          </p>
          {socialLinks.length > 0 && (
            <div className="footer-socials">
              {socialLinks.map((item, idx) => (
                <a
                  key={idx}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-link"
                  aria-label={item.label}
                >
                  <i className={`ti ${item.icon}`} />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Sections */}
        {footerSections.map((section, idx) => (
          <div key={idx} className="footer-section">
            <h4 className="footer-title">{section.title}</h4>
            <ul className="footer-links">
              {section.links.map((link, linkIdx) => (
                <li key={linkIdx}>
                  <Link to={link.path} className="footer-link">
                    <i className={`ti ${link.icon}`} />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Apps & Contact */}
        <div className="footer-apps">
          <h4 className="footer-title">Télécharger l'app</h4>
          <div className="footer-app-buttons">
            <a href="#" className="footer-app-btn" target="_blank" rel="noopener noreferrer">
              <i className="ti ti-brand-apple" />
              <div>
                <span className="footer-app-btn-sub">Télécharger sur</span>
                <span className="footer-app-btn-main">App Store</span>
              </div>
            </a>
            <a href="#" className="footer-app-btn" target="_blank" rel="noopener noreferrer">
              <i className="ti ti-brand-android" />
              <div>
                <span className="footer-app-btn-sub">Télécharger sur</span>
                <span className="footer-app-btn-main">Google Play</span>
              </div>
            </a>
          </div>
          <div className="footer-app-badges">
            <span className="footer-badge footer-badge-parent">
              <i className="ti ti-user" /> Parents
            </span>
            <span className="footer-badge footer-badge-school">
              <i className="ti ti-school" /> Écoles
            </span>
          </div>
          <div className="footer-contact">
            <a href={`mailto:${email}`} className="footer-contact-link">
              <i className="ti ti-mail" /> {email}
            </a>
            {telephone && (
              <a href={`tel:${telephone.replace(/\s/g, '')}`} className="footer-contact-link">
                <i className="ti ti-phone" /> {telephone}
              </a>
            )}
            {contact.adresse && (
              <span className="footer-contact-link" style={{ cursor: "default" }}>
                <i className="ti ti-location" /> {contact.adresse}
              </span>
            )}
            {contact.horaires && (
              <span className="footer-contact-link" style={{ cursor: "default" }}>
                <i className="ti ti-clock" /> {contact.horaires}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <span>© {currentYear} {params.nom_editeur || "Enumera"}. Tous droits réservés.</span>
        <span className="footer-bottom-divider">•</span>
        <span>Made with <i className="ti ti-heart" /> in Burkina Faso</span>
        <span className="footer-bottom-divider">•</span>
        <a href="https://enumera.tech/" target="_blank" rel="noopener noreferrer" className="footer-bottom-link">
          <i className="ti ti-world" /> enumera.tech
        </a>
      </div>

      <style>{`
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

        /* Palette Kamalpha (dupliquée ici : AdminLayout n'est pas monté hors connexion) */
        .footer {
          --pr:    #1B4F80;
          --pr-lt: #2F6DA8;
          --pr-dk: #0E2F52;
          --pr-bg: #E6EEF7;
          --or:    #C08A2E;
          --or-lt: #E3B75A;
          --or-dk: #8A5A14;

          position: relative;
          background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
          color: rgba(255, 255, 255, 0.7);
          padding: 56px 32px 20px;
          overflow: hidden;
        }

        /* Filet dégradé bleu → or : la signature du logo */
        .footer-top-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--pr) 0%, var(--pr-lt) 55%, var(--or) 100%);
        }

        .footer-container {
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1.5fr;
          gap: 40px;
          padding-bottom: 36px;
          border-bottom: 1px solid rgba(227, 183, 90, 0.15);
        }

        .footer-brand {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .footer-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 24px;
          font-weight: 800;
          color: #ffffff;
        }

        /* Même traitement que dans le topbar : icône sur fond blanc, liseré or */
        .footer-logo-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid rgba(192, 138, 46, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
        }

        .footer-logo-icon i {
          font-size: 22px;
          color: var(--pr);
        }

        .footer-description {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.7);
          max-width: 320px;
          margin: 0;
        }

        .footer-socials {
          display: flex;
          gap: 10px;
          margin-top: 4px;
        }

        .footer-social-link {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.08);
          color: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          text-decoration: none;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .footer-social-link:hover {
          background: var(--or-lt);
          color: var(--pr-dk);
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(192, 138, 46, 0.35);
          border-color: var(--or-lt);
        }

        .footer-social-link i {
          font-size: 18px;
        }

        .footer-section {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .footer-title {
          font-size: 13px;
          font-weight: 700;
          color: #ffffff;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin: 0;
          position: relative;
          padding-bottom: 8px;
        }

        /* Petit soulignement or sous les titres */
        .footer-title::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 24px;
          height: 2px;
          background: linear-gradient(90deg, var(--or), var(--or-lt));
          border-radius: 2px;
        }

        .footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .footer-link {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.2s ease;
        }

        .footer-link i {
          font-size: 15px;
          opacity: 0.6;
          color: rgba(255, 255, 255, 0.7);
          transition: all 0.2s ease;
        }

        .footer-link:hover {
          color: #ffffff;
          transform: translateX(4px);
        }

        .footer-link:hover i {
          opacity: 1;
          color: var(--or-lt);
        }

        .footer-apps {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .footer-app-buttons {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .footer-app-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          background: rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          color: #ffffff;
          text-decoration: none;
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s ease;
        }

        .footer-app-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateX(4px);
          border-color: rgba(227, 183, 90, 0.4);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }

        .footer-app-btn i {
          font-size: 28px;
          color: rgba(255, 255, 255, 0.8);
          transition: color 0.3s ease;
        }

        .footer-app-btn:hover i {
          color: var(--or-lt);
        }

        .footer-app-btn div {
          display: flex;
          flex-direction: column;
        }

        .footer-app-btn-sub {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .footer-app-btn-main {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
        }

        .footer-app-badges {
          display: flex;
          gap: 8px;
          margin-top: 4px;
        }

        .footer-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 14px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .footer-badge-parent {
          background: rgba(255, 255, 255, 0.12);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .footer-badge-school {
          background: rgba(192, 138, 46, 0.2);
          color: var(--or-lt);
          border: 1px solid rgba(192, 138, 46, 0.35);
        }

        .footer-contact {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 4px;
        }

        .footer-contact-link {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
        }

        .footer-contact-link:hover {
          color: var(--or-lt);
        }

        .footer-contact-link i {
          color: rgba(255, 255, 255, 0.7);
          transition: color 0.2s ease;
        }

        .footer-contact-link:hover i {
          color: var(--or-lt);
        }

        .footer-bottom {
          max-width: 1280px;
          margin: 20px auto 0;
          padding-top: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.5);
          flex-wrap: wrap;
        }

        .footer-bottom-divider {
          color: rgba(227, 183, 90, 0.4);
        }

        .footer-bottom .ti-heart {
          color: var(--or-lt);
          font-size: 14px;
          vertical-align: middle;
        }

        .footer-bottom-link {
          color: rgba(255, 255, 255, 0.5);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
        }

        .footer-bottom-link:hover {
          color: var(--or-lt);
        }

        @media (max-width: 1024px) {
          .footer-container {
            grid-template-columns: 1fr 1fr 1fr;
          }

          .footer-brand {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 768px) {
          .footer {
            padding: 40px 20px 16px;
          }

          .footer-container {
            grid-template-columns: 1fr;
            gap: 28px;
            padding-bottom: 28px;
          }

          .footer-description {
            max-width: 100%;
          }

          .footer-bottom {
            flex-direction: column;
            gap: 6px;
          }

          .footer-bottom-divider {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .footer {
            padding: 32px 16px 12px;
          }

          .footer-logo {
            font-size: 20px;
          }

          .footer-logo-icon {
            width: 36px;
            height: 36px;
          }

          .footer-logo-icon i {
            font-size: 18px;
          }

          .footer-app-buttons {
            flex-direction: row;
          }

          .footer-app-btn {
            flex: 1;
            padding: 8px 12px;
            gap: 8px;
          }

          .footer-app-btn i {
            font-size: 22px;
          }

          .footer-app-btn-sub {
            font-size: 9px;
          }

          .footer-app-btn-main {
            font-size: 12px;
          }
        }
      `}</style>
    </footer>
  );
};
 
export default Footer;